/**
 * CashflowComparisonTable Component
 *
 * Displays a detailed comparison table for a single candidate's cashflow data.
 * Shows actual vs expected (baseline) values with diff highlighting.
 * Used inline within expandable candidate rows in the values testing page.
 */

import { Badge, Box, Card, HStack, Table, Text } from '@chakra-ui/react';
import { Check, RefreshCw, X } from 'lucide-react';
import React, { useMemo } from 'react';
import type { CashFlow } from '~/types/candidate';
import {
  type CashflowComparisonStats,
  calculateCashflowComparisonStats,
  getCashflowComparisonSummary,
} from './comparisonStats';
import { DiffCell, formatNumber } from './DiffCell';

interface CashflowComparisonTableProps {
  /** Name of the candidate being compared */
  candidateName: string;
  /** Actual cashflows from current calculation */
  actualCashflows: CashFlow[];
  /** Expected cashflows from baseline (null if no baseline) */
  expectedCashflows: CashFlow[] | null;
}

export const CashflowComparisonTable: React.FC<CashflowComparisonTableProps> = ({
  candidateName,
  actualCashflows,
  expectedCashflows,
}) => {
  // Calculate comparison stats
  const comparisonStats: CashflowComparisonStats = useMemo(
    () => calculateCashflowComparisonStats(actualCashflows, expectedCashflows),
    [actualCashflows, expectedCashflows]
  );

  // Create lookup map for expected cashflows by payment date
  const expectedMap = useMemo(() => {
    const map = new Map<string, CashFlow>();
    if (expectedCashflows) {
      for (const cf of expectedCashflows) {
        map.set(cf.paymentDate, cf);
      }
    }
    return map;
  }, [expectedCashflows]);

  // Create lookup for row status by payment date
  const rowStatusMap = useMemo(() => {
    const map = new Map<string, 'matched' | 'mismatched' | 'new'>();
    for (const c of comparisonStats.matched) map.set(c.paymentDate, 'matched');
    for (const c of comparisonStats.mismatched) map.set(c.paymentDate, 'mismatched');
    for (const c of comparisonStats.newRows) map.set(c.paymentDate, 'new');
    return map;
  }, [comparisonStats]);

  if (actualCashflows.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        No cashflows for {candidateName}
      </Text>
    );
  }

  const { overallStatus } = comparisonStats;

  return (
    <Box>
      {/* Header with status */}
      <HStack justify="space-between" mb={2}>
        <HStack gap={2}>
          <Text fontSize="sm" fontWeight="medium">
            {candidateName} Cashflows ({actualCashflows.length} payments)
          </Text>
          <Badge
            colorPalette={
              overallStatus === 'pass'
                ? 'green'
                : overallStatus === 'fail'
                  ? 'red'
                  : overallStatus === 'partial'
                    ? 'orange'
                    : 'yellow'
            }
            size="sm"
          >
            <HStack gap={1}>
              {overallStatus === 'pass' && <Check size={10} />}
              {overallStatus === 'fail' && <X size={10} />}
              {overallStatus === 'partial' && <RefreshCw size={10} />}
              {overallStatus === 'no-baseline' && <RefreshCw size={10} />}
              <Text fontSize="xs">{getCashflowComparisonSummary(comparisonStats)}</Text>
            </HStack>
          </Badge>
        </HStack>
      </HStack>

      <Card.Root>
        <Card.Body p={0}>
          <Box overflowX="auto" maxH="400px" overflowY="auto">
            <Table.Root size="sm" fontSize="10px">
              <Table.Header position="sticky" top={0} bg="white" zIndex={1}>
                <Table.Row>
                  {/* Status */}
                  <Table.ColumnHeader
                    position="sticky"
                    left={0}
                    bg="white"
                    zIndex={2}
                    px={1}
                    py={0.5}
                    whiteSpace="nowrap"
                  >
                    Status
                  </Table.ColumnHeader>
                  {/* Date/Context */}
                  <Table.ColumnHeader
                    position="sticky"
                    left="50px"
                    bg="white"
                    zIndex={2}
                    px={1}
                    py={0.5}
                    whiteSpace="nowrap"
                  >
                    Date
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Tax Year
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    FY End
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Final FY
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Active
                  </Table.ColumnHeader>
                  {/* Millage/Revenue */}
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Mill Rate
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Total Rev (Year)
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Rev %
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Rev Portion
                  </Table.ColumnHeader>
                  {/* Existing Debt */}
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    DRF Start
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Exist Debt Total
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Exist Debt Prin
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Exist Debt Int
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Rev - Debt
                  </Table.ColumnHeader>
                  {/* Bond Context */}
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Int Only
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Int Obligation
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Avail for Bonds
                  </Table.ColumnHeader>
                  {/* Per-Bond Columns */}
                  {actualCashflows[0]?.bondPayments.map((bp) => (
                    <React.Fragment key={bp.bondSeriesId}>
                      <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                        {bp.bondSeriesName} Prin
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                        {bp.bondSeriesName} Int
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                        {bp.bondSeriesName} Total
                      </Table.ColumnHeader>
                    </React.Fragment>
                  ))}
                  {/* Totals */}
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    Total Bond Pmt
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    DRF Adj
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    DRF End
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    DRF Net Chg
                  </Table.ColumnHeader>
                  <Table.ColumnHeader px={1} py={0.5} whiteSpace="nowrap">
                    DRF Net Rev
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {actualCashflows.map((actual) => {
                  const expected = expectedMap.get(actual.paymentDate);
                  const rowStatus = rowStatusMap.get(actual.paymentDate) ?? 'new';

                  return (
                    <Table.Row key={actual.paymentDate}>
                      {/* Status - sticky */}
                      <Table.Cell position="sticky" left={0} bg="white" zIndex={1} px={1} py={0.5}>
                        <Badge
                          size="sm"
                          colorPalette={
                            rowStatus === 'matched'
                              ? 'green'
                              : rowStatus === 'mismatched'
                                ? 'red'
                                : 'yellow'
                          }
                          fontSize="8px"
                        >
                          {rowStatus === 'matched' && <Check size={8} />}
                          {rowStatus === 'mismatched' && <X size={8} />}
                          {rowStatus === 'new' && 'NEW'}
                        </Badge>
                      </Table.Cell>
                      {/* Date - sticky */}
                      <Table.Cell
                        position="sticky"
                        left="50px"
                        bg="white"
                        zIndex={1}
                        fontWeight="medium"
                        fontSize="10px"
                        px={1}
                        py={0.5}
                        whiteSpace="nowrap"
                      >
                        {actual.paymentDate}
                      </Table.Cell>
                      {/* Context */}
                      <DiffCell
                        actual={actual.relatedTaxBillYear}
                        expected={expected?.relatedTaxBillYear}
                        format={(v) => String(v)}
                        size="compact"
                      />
                      <Table.Cell fontSize="10px" px={1} py={0.5} whiteSpace="nowrap">
                        {actual.relatedFiscalYearEndingDate}
                      </Table.Cell>
                      <DiffCell
                        actual={actual.isFinalFiscalYearPayment}
                        expected={expected?.isFinalFiscalYearPayment}
                        format={(v) => (v ? 'Y' : 'N')}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.isActive}
                        expected={expected?.isActive}
                        format={(v) => (v ? 'Y' : 'N')}
                        size="compact"
                      />
                      {/* Millage/Revenue */}
                      <DiffCell
                        actual={actual.millageRate}
                        expected={expected?.millageRate}
                        format={(v) => (v as number).toFixed(4)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.totalRevenueForTaxYear}
                        expected={expected?.totalRevenueForTaxYear}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.paymentRevenuePercentage}
                        expected={expected?.paymentRevenuePercentage}
                        format={(v) => `${((v as number) * 100).toFixed(1)}%`}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.paymentRevenuePortion}
                        expected={expected?.paymentRevenuePortion}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      {/* Existing Debt */}
                      <DiffCell
                        actual={actual.drfStartingBalance}
                        expected={expected?.drfStartingBalance}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.debtPaymentAmount}
                        expected={expected?.debtPaymentAmount}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.existingDebtPrincipal}
                        expected={expected?.existingDebtPrincipal}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.existingDebtInterest}
                        expected={expected?.existingDebtInterest}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.revenueMinusExistingDebt}
                        expected={expected?.revenueMinusExistingDebt}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      {/* Bond Context */}
                      <DiffCell
                        actual={actual.isInterestOnly}
                        expected={expected?.isInterestOnly}
                        format={(v) => (v ? 'Y' : 'N')}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.interestObligation}
                        expected={expected?.interestObligation}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.availableForBonds}
                        expected={expected?.availableForBonds}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      {/* Per-Bond Payments */}
                      {actual.bondPayments.map((bp, bpIndex) => {
                        const expectedBp = expected?.bondPayments?.[bpIndex];
                        return (
                          <React.Fragment key={bp.bondSeriesId}>
                            <DiffCell
                              actual={bp.principal}
                              expected={expectedBp?.principal}
                              format={(v) => formatNumber(v as number)}
                              size="compact"
                            />
                            <DiffCell
                              actual={bp.totalInterest}
                              expected={expectedBp?.totalInterest}
                              format={(v) => formatNumber(v as number)}
                              size="compact"
                            />
                            <DiffCell
                              actual={bp.totalPayment}
                              expected={expectedBp?.totalPayment}
                              format={(v) => formatNumber(v as number)}
                              size="compact"
                            />
                          </React.Fragment>
                        );
                      })}
                      {/* Totals */}
                      <DiffCell
                        actual={actual.totalBondPayment}
                        expected={expected?.totalBondPayment}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.drfAdjustment}
                        expected={expected?.drfAdjustment}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.drfEndingBalance}
                        expected={expected?.drfEndingBalance}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.drfNetChange}
                        expected={expected?.drfNetChange}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                      <DiffCell
                        actual={actual.drfNetRevenue}
                        expected={expected?.drfNetRevenue}
                        format={(v) => formatNumber(v as number)}
                        size="compact"
                      />
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        </Card.Body>
      </Card.Root>

      {/* Missing Cashflows Summary */}
      {comparisonStats.missing.length > 0 && (
        <Box
          mt={2}
          p={3}
          bg="purple.50"
          borderRadius="md"
          border="1px solid"
          borderColor="purple.200"
        >
          <HStack gap={2} mb={2}>
            <Badge colorPalette="purple" size="sm">
              MISSING
            </Badge>
            <Text fontSize="sm" fontWeight="medium">
              {comparisonStats.missing.length} payment date(s) in baseline not found in actual:
            </Text>
          </HStack>
          <HStack gap={2} flexWrap="wrap">
            {comparisonStats.missing.map((m) => (
              <Badge key={m.paymentDate} variant="outline" colorPalette="purple" size="sm">
                {m.paymentDate}
              </Badge>
            ))}
          </HStack>
        </Box>
      )}
    </Box>
  );
};
